import React, { useCallback, useEffect, useState } from "react";
import { FiAlertCircle } from "react-icons/fi";
import { toast } from "react-toastify";
import useCallCenterT from "../../hooks/useCallCenterT";
import PaymentConfirmationEventLogPanel from "../../components/call-center/PaymentConfirmationEventLogPanel";
import {
  getOrganizationPaymentConfirmationConfig,
  testOrganizationPaymentConfirmationConnection,
  updateOrganizationPaymentConfirmationConfig,
} from "../../service/CallCenterPaymentConfirmationConfig";
import PaymentConfirmationConfigForm, {
  buildPaymentConfirmationInitialValues,
  buildPaymentConfirmationPayload,
} from "../../components/call-center/PaymentConfirmationConfigForm";
import {
  LISTING_CONTAINER_CLASS,
  LISTING_PAGE_CLASS,
  LISTING_PRIMARY_BUTTON_CLASS,
} from "../../components/call-center/listing/listingStyles";

const SettingsSkeleton = () => (
  <div className="space-y-5" aria-hidden="true">
    <div className="h-5 w-56 animate-pulse rounded bg-slate-100" />
    <div className="h-11 animate-pulse rounded-xl bg-slate-100" />
    <div className="h-11 animate-pulse rounded-xl bg-slate-100" />
    <div className="h-24 animate-pulse rounded-xl bg-slate-100" />
    <div className="h-11 w-40 animate-pulse rounded-xl bg-slate-100" />
  </div>
);

const CallCenterPaymentConfirmationSettingsPage = () => {
  const t = useCallCenterT();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [organizationId, setOrganizationId] = useState(null);
  const [eventLogRefreshKey, setEventLogRefreshKey] = useState(0);
  const [initialValues, setInitialValues] = useState(buildPaymentConfirmationInitialValues());
  const [secretFlags, setSecretFlags] = useState({
    webhookSecretConfigured: false,
    pollAuthSecretConfigured: false,
    mqPasswordConfigured: false,
  });

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");
      const data = await getOrganizationPaymentConfirmationConfig();
      setOrganizationId(data?.organizationId ?? null);
      setInitialValues(buildPaymentConfirmationInitialValues(data));
      setSecretFlags({
        webhookSecretConfigured: Boolean(data?.webhookSecretConfigured),
        pollAuthSecretConfigured: Boolean(data?.pollAuthSecretConfigured),
        mqPasswordConfigured: Boolean(data?.mqPasswordConfigured),
      });
    } catch {
      setLoadError(t("ccPaymentConfirmationLoadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSubmit = async (values) => {
    try {
      setSaving(true);
      const payload = buildPaymentConfirmationPayload(values, t);
      const updated = await updateOrganizationPaymentConfirmationConfig(payload);
      setOrganizationId(updated?.organizationId ?? organizationId);
      setInitialValues(buildPaymentConfirmationInitialValues(updated));
      setSecretFlags({
        webhookSecretConfigured: Boolean(updated?.webhookSecretConfigured),
        pollAuthSecretConfigured: Boolean(updated?.pollAuthSecretConfigured),
        mqPasswordConfigured: Boolean(updated?.mqPasswordConfigured),
      });
      setEventLogRefreshKey((prev) => prev + 1);
      toast.success(t("ccPaymentConfirmationSaveSuccess"));
    } catch (error) {
      toast.error(error.message || t("ccPaymentConfirmationSaveFailed"));
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      const result = await testOrganizationPaymentConfirmationConnection();
      if (result?.success) {
        toast.success(result.message || t("ccPaymentConfirmationTestSuccess"));
      } else {
        toast.error(result?.message || t("ccPaymentConfirmationTestFailed"));
      }
    } catch (error) {
      toast.error(error.message || t("ccPaymentConfirmationTestFailed"));
    } finally {
      setTestingConnection(false);
    }
  };

  return (
    <div className={LISTING_PAGE_CLASS}>
      <div className={`${LISTING_CONTAINER_CLASS} max-w-4xl`}>
        <header className="mb-6">
          <h1 className="text-[28px] font-bold tracking-tight text-[#111827]">
            {t("ccPaymentConfirmationSettingsTitle")}
          </h1>
          <p className="mt-1 text-sm text-[#6b7280]">{t("ccPaymentConfirmationSettingsSubtitle")}</p>
        </header>

        <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8">
          {loading ? (
            <SettingsSkeleton />
          ) : loadError ? (
            <div className="flex flex-col items-center gap-4 py-8 text-center">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600">
                <FiAlertCircle className="h-6 w-6" />
              </div>
              <p className="text-sm text-red-700">{loadError}</p>
              <button type="button" onClick={loadConfig} className={LISTING_PRIMARY_BUTTON_CLASS}>
                {t("ccRefresh")}
              </button>
            </div>
          ) : (
            <PaymentConfirmationConfigForm
              t={t}
              initialValues={initialValues}
              secretFlags={secretFlags}
              organizationId={organizationId}
              saving={saving}
              onSubmit={handleSubmit}
              onTestConnection={handleTestConnection}
              testingConnection={testingConnection}
            />
          )}
        </div>

        {!loading && !loadError ? (
          <PaymentConfirmationEventLogPanel refreshKey={eventLogRefreshKey} />
        ) : null}
      </div>
    </div>
  );
};

export default CallCenterPaymentConfirmationSettingsPage;
